<template>
  <b-card title="Предложка изменений Notion">
    <div class="d-flex align-items-center mb-1">
      <b-form-select
          v-model="statusFilter"
          :options="statusOptions"
          style="max-width: 220px"
          @change="loadSuggestions(1)"
      />
      <b-form-select
          v-model="entityFilter"
          :options="entityOptions"
          style="max-width: 220px"
          class="ml-1"
          @change="loadSuggestions(1)"
      />
      <span v-if="total !== null" class="ml-1 text-muted">Всего: {{ total }}</span>
    </div>

    <div class="d-flex align-items-center mb-1">
      <b-button
          variant="primary"
          size="sm"
          :disabled="selectedIds.size === 0 || bulkApplying"
          @click="applySelected"
      >
        Одобрить выбранное ({{ selectedIds.size }})
      </b-button>
      <b-button
          variant="warning"
          size="sm"
          class="ml-1"
          :disabled="selectedIds.size === 0 || bulkApplying"
          @click="ignoreSelected"
      >
        Игнорировать выбранное ({{ selectedIds.size }})
      </b-button>
      <b-button
          variant="outline-secondary"
          size="sm"
          class="ml-1"
          :disabled="selectedIds.size === 0 || bulkApplying"
          @click="clearSelection"
      >
        Снять выделение
      </b-button>
    </div>

    <b-overlay :show="isLoading" rounded="sm">
      <b-overlay :show="bulkApplying" rounded="sm">
        <template #overlay>
          <div class="text-center px-3" style="min-width: 260px">
            <div class="mb-1">
              Одобрение: {{ bulkProgress.done }} / {{ bulkProgress.total }}
            </div>
            <b-progress
                :value="bulkProgress.done"
                :max="bulkProgress.total"
                height="1rem"
                striped
                animated
            />
          </div>
        </template>

        <b-alert v-if="!isLoading && rows.length === 0" show variant="info">
          Предложек нет
        </b-alert>

        <b-table
            v-if="rows.length > 0"
            :items="rows"
            :fields="fields"
            responsive
            striped
            small
            hover
        >
          <template #head(select)>
            <b-form-checkbox
                :checked="allSelected"
                :indeterminate="isIndeterminate"
                @change="toggleSelectAll"
            />
          </template>

          <template #cell(select)="data">
            <b-form-checkbox
                v-if="data.item.status === 'pending'"
                :checked="isSelected(data.item.id)"
                @mousedown.native.stop.prevent="onCheckboxMouseDown(data.item.id)"
                @mouseenter.native="onCheckboxMouseEnter(data.item.id)"
                @click.native.stop.prevent
            />
          </template>

          <template #cell(title)="data">
            <!-- Клик по названию работает как клик по чекбоксу (включая протяжку) -->
            <span
                v-if="data.item.status === 'pending'"
                class="cursor-pointer user-select-none"
                @mousedown.prevent="onCheckboxMouseDown(data.item.id)"
                @mouseenter="onCheckboxMouseEnter(data.item.id)"
            >{{ data.item.title }}</span>
            <span v-else>{{ data.item.title }}</span>
            <router-link
                :to="{ name: 'content-suggestion-view', params: { id: data.item.id } }"
                class="ml-50"
                title="Открыть предложку"
            >
              <feather-icon icon="ExternalLinkIcon" size="14" />
            </router-link>
          </template>

          <template #cell(type)="data">
            {{ typeLabel(data.item.type) }}
          </template>

          <template #cell(value)="data">
            <b-badge variant="light-primary">{{ data.item.value }}</b-badge>
          </template>

          <template #cell(status)="data">
            <b-badge :variant="statusVariant(data.item.status)">
              {{ statusLabel(data.item.status) }}
            </b-badge>
          </template>

          <template #cell(created_at)="data">
            {{ formatDateTime(data.item.created_at) }}
          </template>
        </b-table>

        <b-pagination
            v-if="total > perPage"
            v-model="currentPage"
            :total-rows="total"
            :per-page="perPage"
            class="mt-1"
            @change="loadSuggestions"
        />
      </b-overlay>
    </b-overlay>
  </b-card>
</template>

<script>
import {
  BCard,
  BTable,
  BOverlay,
  BAlert,
  BBadge,
  BButton,
  BFormCheckbox,
  BFormSelect,
  BPagination,
  BProgress,
} from 'bootstrap-vue'
import {
  computed, onMounted, onUnmounted, ref,
} from '@vue/composition-api'
import store from '@/store'
import contentStoreModule from '@/views/content/contentStoreModule'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { refreshSuggestionBadge } from '@/views/content/suggestionBadge'

export default {
  components: {
    BCard,
    BTable,
    BOverlay,
    BAlert,
    BBadge,
    BButton,
    BFormCheckbox,
    BFormSelect,
    BPagination,
    BProgress,
  },
  setup() {
    const STORE_MODULE_NAME = 'app-content'
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, contentStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const toast = useToast()

    const isLoading = ref(false)
    const rows = ref([])
    const statusFilter = ref('pending')
    const entityFilter = ref('')
    const currentPage = ref(1)
    const perPage = ref(50)
    const total = ref(null)

    const statusOptions = [
      { value: 'pending', text: 'Ожидают' },
      { value: 'applied', text: 'Применённые' },
      { value: 'ignored', text: 'Игнорированные' },
      { value: 'all', text: 'Все' },
    ]

    const entityOptions = [
      { value: '', text: 'Все' },
      { value: 'artist', text: 'Артисты' },
      { value: 'movie', text: 'Фильмы' },
      { value: 'series', text: 'Сериалы' },
    ]

    const fields = [
      { key: 'select', label: '' },
      { key: 'title', label: 'Название' },
      { key: 'type', label: 'Тип' },
      { key: 'value', label: 'Значение' },
      { key: 'status', label: 'Статус' },
      { key: 'created_at', label: 'Создана' },
    ]

    const statusLabels = { pending: 'Ожидает', applied: 'Применена', ignored: 'Игнорирована' }
    const statusVariants = { pending: 'light-warning', applied: 'light-success', ignored: 'light-secondary' }
    const typeLabels = {
      artist_matrix: 'Матрица артиста',
      artist_letter_count: 'Темы число букв',
      artist_tag: 'Тег артиста',
      artist_country: 'Страна артиста',
      artist_cluster: 'Кластер артиста',
      artist_first_letter: 'Первая буква',
      artist_ru: 'Ру артиста',
      movie_matrix: 'Фильм: матрица',
      movie_tag: 'Фильм: тег',
      movie_first_letter: 'Фильм: первая буква',
      movie_cluster: 'Фильм: кластер',
      movie_word_count: 'Фильм: число слов',
      series_matrix: 'Сериал: матрица',
      series_tag: 'Сериал: тег',
      series_first_letter: 'Сериал: первая буква',
      series_cluster: 'Сериал: кластер',
      series_word_count: 'Сериал: число слов',
    }

    const statusLabel = status => statusLabels[status] || status
    const statusVariant = status => statusVariants[status] || 'light-secondary'
    const typeLabel = type => typeLabels[type] || type

    const formatDateTime = iso => new Date(iso).toLocaleString('ru-RU', {
      timeZone: 'Europe/Moscow',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })

    const showToast = (title, variant) => {
      toast({
        component: ToastificationContent,
        props: {
          title,
          icon: ['danger', 'warning'].includes(variant) ? 'AlertTriangleIcon' : 'CheckCircleIcon',
          variant,
        },
      })
    }

    const errorMessage = (error, fallback) => (
      (error.response && error.response.data && error.response.data.message) || fallback
    )

    // --- Массовое выделение ---
    const selectedIds = ref(new Set())
    const isDragging = ref(false)
    const dragTarget = ref(true)

    const clearSelection = () => {
      selectedIds.value = new Set()
    }

    const isSelected = id => selectedIds.value.has(id)

    const setSelected = (id, checked) => {
      const next = new Set(selectedIds.value)
      if (checked) next.add(id)
      else next.delete(id)
      selectedIds.value = next
    }

    const pendingRows = computed(() => rows.value.filter(row => row.status === 'pending'))

    const allSelected = computed(() => (
      pendingRows.value.length > 0 && pendingRows.value.every(row => selectedIds.value.has(row.id))
    ))

    const isIndeterminate = computed(() => (
      !allSelected.value && pendingRows.value.some(row => selectedIds.value.has(row.id))
    ))

    const toggleSelectAll = checked => {
      const next = new Set(selectedIds.value)
      pendingRows.value.forEach(row => {
        if (checked) next.add(row.id)
        else next.delete(row.id)
      })
      selectedIds.value = next
    }

    // Протяжка мышью: зажали на чекбоксе — запоминаем целевое состояние
    // (обратное текущему у стартовой строки), дальше все строки под курсором
    // до отпускания кнопки мыши получают то же состояние.
    const onCheckboxMouseDown = id => {
      const target = !isSelected(id)
      dragTarget.value = target
      isDragging.value = true
      setSelected(id, target)
    }

    const onCheckboxMouseEnter = id => {
      if (isDragging.value) setSelected(id, dragTarget.value)
    }

    const stopDragging = () => {
      isDragging.value = false
    }

    onMounted(() => document.addEventListener('mouseup', stopDragging))
    onUnmounted(() => document.removeEventListener('mouseup', stopDragging))

    // --- Массовые действия (одобрить / игнорировать) ---
    const bulkApplying = ref(false)
    const bulkProgress = ref({ done: 0, total: 0 })

    const runBulkAction = async (action, actionLabel) => {
      const ids = Array.from(selectedIds.value)
      if (ids.length === 0) return

      bulkApplying.value = true
      bulkProgress.value = { done: 0, total: ids.length }

      let succeeded = 0
      let failed = 0

      // Последовательно, а не параллельно — чтобы не долбить синк с Notion/Planfix пачкой запросов разом.
      for (const id of ids) {
        await store.dispatch(action, { id })
            .then(() => { succeeded += 1 })
            .catch(() => { failed += 1 })
        bulkProgress.value = { done: bulkProgress.value.done + 1, total: ids.length }
      }

      bulkApplying.value = false
      clearSelection()

      const variant = failed > 0 ? 'warning' : 'success'
      const message = failed > 0
          ? `${actionLabel}: ${succeeded}, ошибок: ${failed}`
          : `${actionLabel}: ${succeeded}`
      showToast(message, variant)

      loadSuggestions(currentPage.value)
    }

    const applySelected = () => runBulkAction('app-content/confirmSuggestion', 'Одобрено')
    const ignoreSelected = () => runBulkAction('app-content/ignoreSuggestion', 'Игнорировано')

    const loadSuggestions = (page = 1) => {
      isLoading.value = true
      clearSelection()
      const queryParams = {
        status: statusFilter.value,
        page,
        per_page: perPage.value,
      }
      if (entityFilter.value) {
        queryParams.entity = entityFilter.value
      }
      store.dispatch('app-content/fetchSuggestions', queryParams)
          .then(response => {
            rows.value = response.data.data
            total.value = response.data.total
            currentPage.value = response.data.current_page
            perPage.value = response.data.per_page
            refreshSuggestionBadge()
          })
          .catch(error => showToast(errorMessage(error, 'Ошибка загрузки предложек'), 'danger'))
          .finally(() => {
            isLoading.value = false
          })
    }

    loadSuggestions()

    return {
      isLoading,
      rows,
      fields,
      statusFilter,
      statusOptions,
      entityFilter,
      entityOptions,
      currentPage,
      perPage,
      total,
      statusLabel,
      statusVariant,
      typeLabel,
      formatDateTime,
      loadSuggestions,
      selectedIds,
      isSelected,
      allSelected,
      isIndeterminate,
      toggleSelectAll,
      onCheckboxMouseDown,
      onCheckboxMouseEnter,
      clearSelection,
      applySelected,
      ignoreSelected,
      bulkApplying,
      bulkProgress,
    }
  },
}
</script>
